import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { FormModal } from '@/components/ui/form-modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useCreateNewPositionForIncident,
  useGetPositionsForIncident,
} from 'hooks/incident/useRoster';
import AllowWorkAssignmentField from './AllowWorkAssignmentField';
import {
  OPERATIONS_SECTION_CHIEF_NAME,
  allowsWorkAssignmentByDefault,
} from './allowWorkAssignment';
import {
  SearchableSelect,
  type SearchableSelectOption,
} from './SearchableSelect';

type AddChildPositionDialogProps = {
  show: boolean;
  /** When set, the new seat reports to this one. When omitted the user picks. */
  parentPositionId?: string | null;
  onClose: () => void;
};

export default function AddChildPositionDialog({
  show,
  parentPositionId = null,
  onClose,
}: AddChildPositionDialogProps) {
  const { data: positions = [] } = useGetPositionsForIncident();
  const { mutate: createNew, isPending } = useCreateNewPositionForIncident();
  const [seatName, setSeatName] = useState('');
  const [isWorkgroup, setIsWorkgroup] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportsTo, setReportsTo] = useState<SearchableSelectOption | null>(
    null
  );
  const seededToken = useRef<string | null>(null);
  const parentId = parentPositionId ?? reportsTo?.value ?? null;

  useEffect(() => {
    const token = `${parentId ?? ''}:${positions.length}`;
    if (seededToken.current === token) return;
    if (parentId && positions.length === 0) return;
    seededToken.current = token;
    setIsWorkgroup(allowsWorkAssignmentByDefault(parentId, positions));
  }, [parentId, positions]);

  const parentName =
    positions.find((position) => position.id === parentPositionId)?.name ??
    'selected seat';
  const managerOptions = positions.map((position) => ({
    value: position.id,
    label: position.name,
  }));

  const onSubmit = () => {
    const name = seatName.trim();
    if (!name) {
      setError('Seat name is required');
      return;
    }
    createNew(
      {
        name,
        reportsTo: parentPositionId ?? reportsTo?.value,
        isWorkgroup,
      },
      {
        onSuccess: () => onClose(),
      }
    );
  };

  return (
    <FormModal
      open={show}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={parentPositionId ? `Add position under ${parentName}` : 'Add Seat'}
      formProps={{
        onSubmit: (event) => {
          event.preventDefault();
          onSubmit();
        },
      }}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isPending}>
            Add position
          </Button>
        </>
      }
    >
      <div className="tw:space-y-4" data-tour="roster-add-position">
        <div className="tw:space-y-2">
          <Label htmlFor="add-child-seat-name">New seat name</Label>
          <Input
            id="add-child-seat-name"
            placeholder="Enter seat name"
            value={seatName}
            aria-invalid={!!error}
            onChange={(event) => {
              setSeatName(event.target.value);
              if (error) setError(null);
              if (event.target.value.trim() === OPERATIONS_SECTION_CHIEF_NAME) {
                setIsWorkgroup(true);
              }
            }}
          />
          {error ? (
            <p className="tw:text-sm tw:text-destructive">{error}</p>
          ) : null}
        </div>
        {parentPositionId ? (
          <p className="tw:text-sm tw:text-muted-foreground">
            Reports to:{' '}
            <span className="tw:font-medium tw:text-foreground">
              {parentName}
            </span>
          </p>
        ) : (
          <SearchableSelect
            id="add-seat-reports-to"
            label="Reports to:"
            options={managerOptions}
            value={reportsTo}
            onChange={(option) => {
              setReportsTo(option);
              setIsWorkgroup(
                allowsWorkAssignmentByDefault(
                  option?.value,
                  positions,
                  seatName
                )
              );
            }}
            placeholder="Select a seat"
            clearable
          />
        )}
        <AllowWorkAssignmentField
          id="add-child-workgroup"
          checked={isWorkgroup}
          onCheckedChange={setIsWorkgroup}
        />
      </div>
    </FormModal>
  );
}
